import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  RadialBarChart,
  RadialBar,
  Cell,
} from 'recharts';
import {
  ClockIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CalendarIcon,
  BeakerIcon,
  SunIcon,
  CloudIcon,
  GlobeAltIcon,
  MapPinIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon,
  ScaleIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { StarIcon } from '@heroicons/react/24/solid';
import { farmApi, Farm } from '../lib/api';
import api from '../lib/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface YearlyNDVI {
  year: number;
  season: string;
  avgNdvi: number;
  maxNdvi: number;
  minNdvi: number;
  cloudFreeDays: number;
}

interface LandChange {
  year: number;
  type: string;
  description: string;
  confidence: number;
}

interface HistoricalData {
  farmId: string;
  farmName: string;
  satellite: string;
  dataSource: string;
  startYear: number;
  endYear: number;
  yearlyData: YearlyNDVI[];
  changes: LandChange[];
  overallStats: {
    meanNdvi: number;
    maxNdvi: number;
    minNdvi: number;
    trend: number;
  };
  interpretation: {
    trend: string;
    healthStatus: string;
  };
}

interface TimelineData {
  date: string;
  year: number;
  month: string;
  ndvi: number;
  evi: number;
  precipitation: number;
  temperature: number;
}

interface CropRecommendation {
  cropId: string;
  name: string;
  scientificName?: string;
  category: string;
  suitabilityScore: number;
  confidence: number;
  reasoning: string[];
  risks: string[];
  expectedYield: string;
  plantingWindow: {
    start: string;
    end: string;
    description: string;
  };
  waterRequirement: string;
  profitability: string;
  marketDemand: string;
  description: string;
}

interface RecommendationData {
  farmId: string;
  farmName: string;
  location: { lat: number; lon: number };
  analysisYears: number;
  dataPoints: number;
  climate: {
    avgTemperature: number;
    annualPrecipitation: number;
    growingSeasonDays: number;
    frostFreeDays: number;
  };
  historicalNdvi: {
    average: number | null;
    trend: number | null;
  };
  recommendations: CropRecommendation[];
}

interface DetectedCrop {
  year: number;
  cropId: string;
  cropName: string;
  confidence: number;
  peakNdvi: number;
  peakMonth: number;
  alternativeCrops: { cropId: string; cropName: string; confidence: number }[];
  notes?: string;
}

interface CropHistoryData {
  farmId: string;
  farmName: string;
  yearsAnalyzed: number;
  fromCache: boolean;
  detectedCrops: DetectedCrop[];
  rotationPattern: string | null;
  summary: {
    mostCommonCrop: string;
    diversityScore: number;
    consistencyScore: number;
  };
  availableSignatures: { id: string; name: string; description: string }[];
}

interface YieldPrediction {
  cropType: string;
  expectedYield: number;
  unit: string;
  range: { lower: number; upper: number };
  percentile: number;
  totalHarvestTons: number;
  comparedToRegional: number | null;
}

interface YieldEstimateData {
  farmId: string;
  farmName: string;
  areaHectares: number;
  yearsOfData: number;
  ndviStats: {
    max: number;
    mean: number;
    variability: number;
  };
  climate: {
    avgTemperature: number;
    annualPrecipitation: number;
    growingSeasonDays: number;
  };
  predictions: YieldPrediction[];
}

// Score progress circle component
const ScoreCircle = ({ score, size = 60 }: { score: number; size?: number }) => {
  const getColor = (s: number) => {
    if (s >= 80) return '#22c55e';
    if (s >= 60) return '#eab308';
    if (s >= 40) return '#f97316';
    return '#ef4444';
  };

  const data = [{ value: score, fill: getColor(score) }];

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <RadialBarChart
        width={size}
        height={size}
        cx={size / 2}
        cy={size / 2}
        innerRadius={size * 0.35}
        outerRadius={size * 0.5}
        barSize={8}
        data={data}
        startAngle={90}
        endAngle={-270}
      >
        <RadialBar
          background={{ fill: '#e5e7eb' }}
          dataKey="value"
          cornerRadius={10}
        />
      </RadialBarChart>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold">{score.toFixed(0)}</span>
      </div>
    </div>
  );
};

type ViewMode = 'history' | 'crop-history' | 'recommendations' | 'yields';

export default function HistoricalAnalysis() {
  const [selectedFarmId, setSelectedFarmId] = useState<string>('');
  const [yearsBack, setYearsBack] = useState(10);
  const [satellite, setSatellite] = useState('sentinel-2');
  const [viewMode, setViewMode] = useState<ViewMode>('history');
  const [downloading, setDownloading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Ref for PDF capture
  const contentRef = useRef<HTMLDivElement>(null);

  // Helper for backward compatibility
  const showRecommendations = viewMode === 'recommendations';

  // Generate PDF Report
  const handleDownloadPdf = async () => {
    if (!contentRef.current || !selectedFarmId) return;

    setGeneratingPdf(true);
    toast.loading('Generating PDF report...', { id: 'pdf-generation' });

    try {
      const element = contentRef.current;

      // Capture the content as canvas
      const canvas = await html2canvas(element, {
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f8fafc',
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/png');

      // Calculate PDF dimensions (A4)
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Add header
      pdf.setFillColor(37, 99, 235); // Blue header
      pdf.rect(0, 0, pdfWidth, 25, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('KENAGRI Analysis Report', 10, 15);

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const viewModeLabels: Record<ViewMode, string> = {
        'history': 'Historical NDVI Analysis',
        'crop-history': 'Crop History Detection',
        'recommendations': 'Crop Recommendations',
        'yields': 'Yield Predictions',
      };
      pdf.text(viewModeLabels[viewMode], 10, 22);

      // Add metadata
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, pdfWidth - 60, 15);
      pdf.text(`Farm: ${selectedFarm?.name || 'N/A'}`, pdfWidth - 60, 22);

      // Calculate image dimensions to fit page
      const imgWidth = pdfWidth - 20; // 10mm margin on each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add content image (may need multiple pages)
      let yPosition = 30;
      const maxHeight = pdfHeight - 40; // Leave margin at bottom

      if (imgHeight <= maxHeight) {
        // Fits on one page
        pdf.addImage(imgData, 'PNG', 10, yPosition, imgWidth, imgHeight);
      } else {
        // Multiple pages needed
        let remainingHeight = imgHeight;
        let sourceY = 0;

        while (remainingHeight > 0) {
          const pageHeight = Math.min(remainingHeight, maxHeight);
          const sourceHeight = (pageHeight / imgHeight) * canvas.height;

          // Create a cropped canvas for this page
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = sourceHeight;
          const ctx = pageCanvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(
              canvas,
              0, sourceY, canvas.width, sourceHeight,
              0, 0, canvas.width, sourceHeight
            );
            const pageImgData = pageCanvas.toDataURL('image/png');
            pdf.addImage(pageImgData, 'PNG', 10, yPosition, imgWidth, pageHeight);
          }

          remainingHeight -= pageHeight;
          sourceY += sourceHeight;

          if (remainingHeight > 0) {
            pdf.addPage();
            yPosition = 10;
          }
        }
      }

      // Add footer on last page
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        'Generated by KENAGRI - Agricultural Intelligence Platform',
        pdfWidth / 2,
        pdfHeight - 5,
        { align: 'center' }
      );

      // Save the PDF
      const filename = `kenagri-${viewMode}-report-${selectedFarm?.name?.replace(/\s+/g, '-').toLowerCase() || 'farm'}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

      toast.success('PDF report downloaded!', { id: 'pdf-generation' });
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF. Please try again.', { id: 'pdf-generation' });
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Download report function
  const handleDownload = async (type: 'recommendations' | 'ndvi', format: 'csv' | 'json') => {
    if (!selectedFarmId) return;

    setDownloading(true);
    try {
      const currentYear = new Date().getFullYear();
      let url = '';

      if (type === 'recommendations') {
        url = `/history/farm/${selectedFarmId}/recommendations/download?format=${format}&years=${yearsBack}`;
      } else {
        url = `/history/farm/${selectedFarmId}/ndvi/download?format=${format}&startYear=${currentYear - yearsBack}&endYear=${currentYear}&satellite=${satellite}`;
      }

      const response = await api.get(url, { responseType: 'blob' });

      // Get filename from content-disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `report-${new Date().toISOString().split('T')[0]}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'json' ? 'application/json' : 'text/csv',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Report downloaded successfully!`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Force refresh data
  const handleRefresh = async () => {
    if (!selectedFarmId) return;

    try {
      if (showRecommendations) {
        await api.get(`/history/farm/${selectedFarmId}/recommendations?years=${yearsBack}&forceRefresh=true`);
      } else {
        const currentYear = new Date().getFullYear();
        await api.get(`/history/farm/${selectedFarmId}/ndvi?startYear=${currentYear - yearsBack}&endYear=${currentYear}&satellite=${satellite}&forceRefresh=true`);
      }
      toast.success('Data refreshed from satellite!');
      // Refetch queries
      window.location.reload();
    } catch (error) {
      toast.error('Failed to refresh data');
    }
  };

  // Fetch farms list
  const { data: farms = [] } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  // Fetch historical data
  const currentYear = new Date().getFullYear();
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['history', selectedFarmId, yearsBack, satellite],
    queryFn: async () => {
      const response = await api.get<HistoricalData>(
        `/history/farm/${selectedFarmId}/ndvi?startYear=${currentYear - yearsBack}&endYear=${currentYear}&satellite=${satellite}`
      );
      return response.data;
    },
    enabled: !!selectedFarmId && !showRecommendations,
  });

  // Fetch timeline data
  const { data: timelineData } = useQuery({
    queryKey: ['timeline', selectedFarmId, yearsBack],
    queryFn: async () => {
      const response = await api.get<{ timeline: TimelineData[] }>(
        `/history/farm/${selectedFarmId}/timeline?startYear=${currentYear - Math.min(yearsBack, 5)}&endYear=${currentYear}`
      );
      return response.data;
    },
    enabled: !!selectedFarmId && !showRecommendations,
  });

  // Fetch available years
  const { data: availableYears } = useQuery({
    queryKey: ['available-years'],
    queryFn: async () => {
      const response = await api.get('/history/available-years');
      return response.data;
    },
  });

  // Fetch crop recommendations
  const { data: recommendationData, isLoading: recommendationsLoading } = useQuery({
    queryKey: ['recommendations', selectedFarmId, yearsBack],
    queryFn: async () => {
      const response = await api.get<RecommendationData>(
        `/history/farm/${selectedFarmId}/recommendations?yearsOfData=${yearsBack}`
      );
      return response.data;
    },
    enabled: !!selectedFarmId && viewMode === 'recommendations',
  });

  // Fetch crop history (detected historical crops)
  const { data: cropHistoryData, isLoading: cropHistoryLoading } = useQuery({
    queryKey: ['crop-history', selectedFarmId, yearsBack],
    queryFn: async () => {
      const response = await api.get<CropHistoryData>(
        `/history/farm/${selectedFarmId}/crop-history?years=${yearsBack}`
      );
      return response.data;
    },
    enabled: !!selectedFarmId && viewMode === 'crop-history',
  });

  // Fetch yield predictions
  const { data: yieldData, isLoading: yieldLoading } = useQuery({
    queryKey: ['yield-estimate', selectedFarmId, yearsBack],
    queryFn: async () => {
      const response = await api.get<YieldEstimateData>(
        `/history/farm/${selectedFarmId}/yield-estimate?years=${yearsBack}`
      );
      return response.data;
    },
    enabled: !!selectedFarmId && viewMode === 'yields',
  });

  const getNdviColor = (ndvi: number) => {
    if (ndvi < 0.3) return 'text-red-600';
    if (ndvi < 0.5) return 'text-yellow-600';
    if (ndvi < 0.7) return 'text-green-600';
    return 'text-green-700';
  };

  const getNdviBg = (ndvi: number) => {
    if (ndvi < 0.3) return 'from-red-500 to-red-600';
    if (ndvi < 0.5) return 'from-yellow-500 to-yellow-600';
    if (ndvi < 0.7) return 'from-green-500 to-green-600';
    return 'from-green-600 to-green-700';
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0.01) return <ArrowTrendingUpIcon className="h-6 w-6 text-green-500" />;
    if (trend < -0.01) return <ArrowTrendingDownIcon className="h-6 w-6 text-red-500" />;
    return <ChartBarIcon className="h-6 w-6 text-gray-500" />;
  };

  const getSuitabilityGradient = (score: number) => {
    if (score >= 80) return 'from-green-50 to-emerald-50 border-green-200 hover:border-green-300';
    if (score >= 60) return 'from-yellow-50 to-amber-50 border-yellow-200 hover:border-yellow-300';
    if (score >= 40) return 'from-orange-50 to-amber-50 border-orange-200 hover:border-orange-300';
    return 'from-red-50 to-rose-50 border-red-200 hover:border-red-300';
  };

  const getSuitabilityBadge = (score: number) => {
    if (score >= 80) return { text: 'Excellent Match', color: 'bg-gradient-to-r from-green-500 to-emerald-500', icon: '🌟' };
    if (score >= 60) return { text: 'Good Match', color: 'bg-gradient-to-r from-yellow-500 to-amber-500', icon: '👍' };
    if (score >= 40) return { text: 'Fair Match', color: 'bg-gradient-to-r from-orange-500 to-amber-500', icon: '🤔' };
    return { text: 'Poor Match', color: 'bg-gradient-to-r from-red-500 to-rose-500', icon: '⚠️' };
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      grain: '🌾',
      legume: '🫘',
      oilseed: '🌻',
      forage: '🌿',
      root: '🥔',
      fiber: '🧵',
    };
    return icons[category] || '🌱';
  };

  const selectedFarm = farms.find((f: Farm) => f.id === selectedFarmId);
  const isLoading = historyLoading || recommendationsLoading || cropHistoryLoading || yieldLoading;

  // Get month name from number
  const getMonthName = (monthNum: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNum - 1] || 'N/A';
  };

  // Get crop emoji
  const getCropEmoji = (cropId: string) => {
    const emojis: Record<string, string> = {
      corn: '🌽',
      wheat: '🌾',
      soybeans: '🫘',
      rice: '🍚',
      barley: '🌾',
      cotton: '🧵',
      sunflower: '🌻',
      potatoes: '🥔',
      alfalfa: '🌿',
      pasture: '🌱',
      fallow: '🏜️',
      canola: '💛',
    };
    return emojis[cropId] || '🌱';
  };

  // Get confidence color
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-100';
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-orange-600 bg-orange-100';
  };

  // Get yield percentile color
  const getPercentileColor = (percentile: number) => {
    if (percentile >= 75) return '#22c55e';
    if (percentile >= 50) return '#eab308';
    if (percentile >= 25) return '#f97316';
    return '#ef4444';
  };

  // Get header gradient based on view mode
  const getHeaderGradient = () => {
    switch (viewMode) {
      case 'crop-history': return 'bg-gradient-to-r from-amber-600 via-orange-600 to-yellow-600';
      case 'recommendations': return 'bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600';
      case 'yields': return 'bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600';
      default: return 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600';
    }
  };

  // Get header icon based on view mode
  const getHeaderIcon = () => {
    switch (viewMode) {
      case 'crop-history': return <ClipboardDocumentListIcon className="h-8 w-8 text-white" />;
      case 'recommendations': return <SparklesIcon className="h-8 w-8 text-white" />;
      case 'yields': return <ScaleIcon className="h-8 w-8 text-white" />;
      default: return <ClockIcon className="h-8 w-8 text-white" />;
    }
  };

  // Get header title and description
  const getHeaderContent = () => {
    switch (viewMode) {
      case 'crop-history':
        return {
          title: 'Crop History Detection',
          description: 'AI-detected crops planted in previous years from satellite patterns'
        };
      case 'recommendations':
        return {
          title: 'Crop Recommendations',
          description: 'AI-powered crop suggestions based on satellite data'
        };
      case 'yields':
        return {
          title: 'Yield Predictions',
          description: 'Expected yields for different crops on this land'
        };
      default:
        return {
          title: 'Historical Analysis',
          description: 'Vegetation trends and land use patterns over time'
        };
    }
  };

  const headerContent = getHeaderContent();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Header */}
      <div className={`relative overflow-hidden ${getHeaderGradient()}`}>
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-white/20 backdrop-blur-sm">
                {getHeaderIcon()}
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                  {headerContent.title}
                </h1>
                <p className="text-white/80 mt-1">
                  {headerContent.description}
                </p>
              </div>
            </div>

            {/* Toggle Tabs - Desktop */}
            <div className="hidden lg:flex bg-white/10 backdrop-blur-sm rounded-xl p-1">
              <button
                onClick={() => setViewMode('history')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  viewMode === 'history'
                    ? 'bg-white text-gray-900 shadow-lg'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <ClockIcon className="h-4 w-4" />
                History
              </button>
              <button
                onClick={() => setViewMode('crop-history')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  viewMode === 'crop-history'
                    ? 'bg-white text-gray-900 shadow-lg'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <ClipboardDocumentListIcon className="h-4 w-4" />
                Past Crops
              </button>
              <button
                onClick={() => setViewMode('recommendations')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  viewMode === 'recommendations'
                    ? 'bg-white text-gray-900 shadow-lg'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <SparklesIcon className="h-4 w-4" />
                Suggestions
              </button>
              <button
                onClick={() => setViewMode('yields')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  viewMode === 'yields'
                    ? 'bg-white text-gray-900 shadow-lg'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <ScaleIcon className="h-4 w-4" />
                Yields
              </button>
            </div>
          </div>

          {/* Mobile Toggle */}
          <div className="lg:hidden mt-4 flex flex-wrap bg-white/10 backdrop-blur-sm rounded-xl p-1 gap-1">
            <button
              onClick={() => setViewMode('history')}
              className={`flex-1 min-w-[45%] flex items-center justify-center gap-1 px-2 py-2 rounded-lg font-medium transition-all text-xs ${
                viewMode === 'history'
                  ? 'bg-white text-gray-900 shadow-lg'
                  : 'text-white/80'
              }`}
            >
              <ClockIcon className="h-4 w-4" />
              History
            </button>
            <button
              onClick={() => setViewMode('crop-history')}
              className={`flex-1 min-w-[45%] flex items-center justify-center gap-1 px-2 py-2 rounded-lg font-medium transition-all text-xs ${
                viewMode === 'crop-history'
                  ? 'bg-white text-gray-900 shadow-lg'
                  : 'text-white/80'
              }`}
            >
              <ClipboardDocumentListIcon className="h-4 w-4" />
              Past Crops
            </button>
            <button
              onClick={() => setViewMode('recommendations')}
              className={`flex-1 min-w-[45%] flex items-center justify-center gap-1 px-2 py-2 rounded-lg font-medium transition-all text-xs ${
                viewMode === 'recommendations'
                  ? 'bg-white text-gray-900 shadow-lg'
                  : 'text-white/80'
              }`}
            >
              <SparklesIcon className="h-4 w-4" />
              Suggestions
            </button>
            <button
              onClick={() => setViewMode('yields')}
              className={`flex-1 min-w-[45%] flex items-center justify-center gap-1 px-2 py-2 rounded-lg font-medium transition-all text-xs ${
                viewMode === 'yields'
                  ? 'bg-white text-gray-900 shadow-lg'
                  : 'text-white/80'
              }`}
            >
              <ScaleIcon className="h-4 w-4" />
              Yields
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6 -mt-4">
        {/* Controls Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-gray-100 rounded-lg">
              <GlobeAltIcon className="h-5 w-5 text-gray-600" />
            </div>
            <h2 className="font-semibold text-gray-900">Analysis Settings</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Farm Selector */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <MapPinIcon className="h-4 w-4 text-gray-400" />
                Select Farm
              </label>
              <select
                value={selectedFarmId}
                onChange={(e) => setSelectedFarmId(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer"
              >
                <option value="">Choose a farm...</option>
                {farms.map((farm: Farm) => (
                  <option key={farm.id} value={farm.id}>
                    {farm.name} ({farm.areaHectares?.toFixed(1)} ha)
                  </option>
                ))}
              </select>
            </div>

            {/* Years Selector */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <CalendarIcon className="h-4 w-4 text-gray-400" />
                Analysis Period
              </label>
              <select
                value={yearsBack}
                onChange={(e) => setYearsBack(Number(e.target.value))}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer"
              >
                <option value={5}>Last 5 years</option>
                <option value={10}>Last 10 years</option>
                <option value={15}>Last 15 years</option>
                <option value={20}>Last 20 years</option>
              </select>
            </div>

            {/* Satellite Selector */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <GlobeAltIcon className="h-4 w-4 text-gray-400" />
                Satellite Source
              </label>
              <select
                value={satellite}
                onChange={(e) => setSatellite(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer"
              >
                <option value="sentinel-2">Sentinel-2 (10m res)</option>
                <option value="landsat">Landsat (30m res)</option>
                <option value="planet">Planet (3m res)</option>
              </select>
            </div>

            {/* Info Card */}
            <div className="flex flex-col justify-center">
              {availableYears && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center gap-2 text-blue-700">
                    <InformationCircleIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">
                      {satellite === 'sentinel-2' && `Data: ${availableYears.sentinel2?.startYear}-${availableYears.sentinel2?.endYear}`}
                      {satellite === 'landsat' && `Data: ${availableYears.landsat?.startYear}-${availableYears.landsat?.endYear}`}
                      {satellite === 'planet' && `Data: ${availableYears.planet?.startYear}-${availableYears.planet?.endYear}`}
                    </span>
                  </div>
                  <p className="text-xs text-blue-600/70 mt-1">
                    {satellite === 'sentinel-2' && 'High resolution multispectral'}
                    {satellite === 'landsat' && 'Long historical archive'}
                    {satellite === 'planet' && 'Daily high-res imagery'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Download & Refresh Toolbar */}
          {selectedFarmId && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <DocumentTextIcon className="h-4 w-4" />
                <span>Export your analysis data</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  disabled={downloading}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Refresh Data
                </button>

                {/* Download Dropdown */}
                <div className="relative group">
                  <button
                    disabled={downloading || generatingPdf}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                  >
                    {downloading || generatingPdf ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {generatingPdf ? 'Generating PDF...' : 'Downloading...'}
                      </>
                    ) : (
                      <>
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        Download Report
                      </>
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    {/* PDF Report - Full Page */}
                    <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Full Page Report
                    </div>
                    <button
                      onClick={handleDownloadPdf}
                      disabled={generatingPdf}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 text-red-600" />
                      Download as PDF
                      <span className="ml-auto text-xs text-gray-400">Full page snapshot</span>
                    </button>

                    <div className="my-2 border-t border-gray-100" />

                    {/* Data Export */}
                    <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Data Export
                    </div>
                    <button
                      onClick={() => handleDownload(showRecommendations ? 'recommendations' : 'ndvi', 'csv')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <DocumentTextIcon className="h-4 w-4 text-green-600" />
                      Download as CSV
                      <span className="ml-auto text-xs text-gray-400">Excel-ready</span>
                    </button>
                    <button
                      onClick={() => handleDownload(showRecommendations ? 'recommendations' : 'ndvi', 'json')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                      Download as JSON
                      <span className="ml-auto text-xs text-gray-400">API format</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* No Farm Selected State */}
        {!selectedFarmId && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12">
            <div className="text-center max-w-md mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPinIcon className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Farm</h3>
              <p className="text-gray-500">
                Choose a farm from the dropdown above to view {showRecommendations ? 'crop recommendations' : 'historical analysis'}
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {selectedFarmId && isLoading && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12">
            <div className="text-center">
              <div className="relative w-16 h-16 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
                <div className="absolute inset-0 rounded-full border-4 border-t-blue-600 animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {viewMode === 'recommendations' && 'Analyzing Crop Suitability...'}
                {viewMode === 'crop-history' && 'Detecting Historical Crops...'}
                {viewMode === 'yields' && 'Calculating Yield Predictions...'}
                {viewMode === 'history' && 'Loading Historical Data...'}
              </h3>
              <p className="text-gray-500">Processing satellite imagery and calculating indices</p>
            </div>
          </div>
        )}

        {/* Content area for PDF capture */}
        <div ref={contentRef}>
        {/* ==================== CROP RECOMMENDATIONS VIEW ==================== */}
        {showRecommendations && selectedFarmId && recommendationData && !recommendationsLoading && (
          <div className="space-y-6">
            {/* Climate & Location Summary */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 px-6 py-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BeakerIcon className="h-5 w-5" />
                  Climate Profile for {selectedFarm?.name}
                </h3>
                <p className="text-purple-100 text-sm">Based on {recommendationData.dataPoints} years of satellite data</p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
                    <div className="flex items-center gap-2 mb-2">
                      <SunIcon className="h-5 w-5 text-orange-500" />
                      <span className="text-sm font-medium text-orange-700">Temperature</span>
                    </div>
                    <div className="text-3xl font-bold text-orange-900">
                      {recommendationData.climate.avgTemperature}°C
                    </div>
                    <p className="text-xs text-orange-600 mt-1">Annual average</p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <CloudIcon className="h-5 w-5 text-blue-500" />
                      <span className="text-sm font-medium text-blue-700">Rainfall</span>
                    </div>
                    <div className="text-3xl font-bold text-blue-900">
                      {recommendationData.climate.annualPrecipitation}
                      <span className="text-lg font-normal">mm</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Annual precipitation</p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarIcon className="h-5 w-5 text-green-500" />
                      <span className="text-sm font-medium text-green-700">Growing Season</span>
                    </div>
                    <div className="text-3xl font-bold text-green-900">
                      {recommendationData.climate.growingSeasonDays}
                      <span className="text-lg font-normal">d</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">Optimal growing days</p>
                  </div>

                  <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-100">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircleIcon className="h-5 w-5 text-teal-500" />
                      <span className="text-sm font-medium text-teal-700">Frost-Free</span>
                    </div>
                    <div className="text-3xl font-bold text-teal-900">
                      {recommendationData.climate.frostFreeDays}
                      <span className="text-lg font-normal">d</span>
                    </div>
                    <p className="text-xs text-teal-600 mt-1">Safe planting window</p>
                  </div>
                </div>

                {/* NDVI Summary */}
                {recommendationData.historicalNdvi.average !== null && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${getNdviBg(recommendationData.historicalNdvi.average)}`} />
                      <span className="text-sm text-gray-600">
                        Historical NDVI: <span className={`font-semibold ${getNdviColor(recommendationData.historicalNdvi.average)}`}>
                          {recommendationData.historicalNdvi.average.toFixed(3)}
                        </span>
                      </span>
                    </div>
                    {recommendationData.historicalNdvi.trend !== null && (
                      <div className="flex items-center gap-2">
                        {getTrendIcon(recommendationData.historicalNdvi.trend)}
                        <span className={`text-sm font-medium ${
                          recommendationData.historicalNdvi.trend > 0 ? 'text-green-600' :
                          recommendationData.historicalNdvi.trend < 0 ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {recommendationData.historicalNdvi.trend > 0 ? '+' : ''}
                          {(recommendationData.historicalNdvi.trend * 100).toFixed(2)}%/yr trend
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Recommended Crops */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5" />
                  Top Crop Recommendations
                </h3>
                <p className="text-green-100 text-sm">Ranked by suitability for your land conditions</p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {recommendationData.recommendations.map((rec, idx) => {
                    const badge = getSuitabilityBadge(rec.suitabilityScore);
                    return (
                      <div
                        key={idx}
                        className={`bg-gradient-to-br ${getSuitabilityGradient(rec.suitabilityScore)} rounded-2xl border-2 p-5 transition-all hover:shadow-lg`}
                      >
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start gap-3">
                            <div className="text-3xl">{getCategoryIcon(rec.category)}</div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-lg font-bold text-gray-900">{rec.name}</h4>
                                {idx === 0 && (
                                  <StarIcon className="h-5 w-5 text-yellow-500" />
                                )}
                              </div>
                              {rec.scientificName && (
                                <p className="text-xs italic text-gray-500">{rec.scientificName}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold text-white ${badge.color} shadow-sm`}>
                                  {badge.icon} {badge.text}
                                </span>
                                <span className="px-2 py-0.5 rounded-full text-xs bg-white/80 text-gray-600 capitalize border">
                                  {rec.category}
                                </span>
                              </div>
                            </div>
                          </div>
                          <ScoreCircle score={rec.suitabilityScore} size={65} />
                        </div>

                        {/* Description */}
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed">{rec.description}</p>

                        {/* Key Stats Grid */}
                        <div className="grid grid-cols-4 gap-2 mb-4">
                          <div className="bg-white/60 rounded-lg p-2.5 text-center border border-white/50">
                            <div className="text-xs text-gray-500 mb-0.5">Yield</div>
                            <div className="text-sm font-semibold text-gray-900">{rec.expectedYield}</div>
                          </div>
                          <div className="bg-white/60 rounded-lg p-2.5 text-center border border-white/50">
                            <div className="text-xs text-gray-500 mb-0.5">Water</div>
                            <div className="text-sm font-semibold text-gray-900 capitalize">{rec.waterRequirement}</div>
                          </div>
                          <div className="bg-white/60 rounded-lg p-2.5 text-center border border-white/50">
                            <div className="text-xs text-gray-500 mb-0.5">Profit</div>
                            <div className="text-sm font-semibold text-gray-900 capitalize">{rec.profitability}</div>
                          </div>
                          <div className="bg-white/60 rounded-lg p-2.5 text-center border border-white/50">
                            <div className="text-xs text-gray-500 mb-0.5">Confidence</div>
                            <div className="text-sm font-semibold text-gray-900">{(rec.confidence * 100).toFixed(0)}%</div>
                          </div>
                        </div>

                        {/* Planting Window */}
                        <div className="flex items-center gap-2 mb-4 p-3 bg-white/60 rounded-lg border border-white/50">
                          <CalendarIcon className="h-5 w-5 text-green-600" />
                          <div>
                            <span className="text-sm font-medium text-gray-700">Plant: </span>
                            <span className="text-sm text-gray-600">{rec.plantingWindow.start} - {rec.plantingWindow.end}</span>
                          </div>
                        </div>

                        {/* Reasoning */}
                        <div className="mb-3">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-semibold text-gray-700">Why it's suitable:</span>
                          </div>
                          <ul className="text-sm text-gray-600 space-y-1 ml-6">
                            {rec.reasoning.slice(0, 3).map((reason, ridx) => (
                              <li key={ridx} className="list-disc">{reason}</li>
                            ))}
                          </ul>
                        </div>

                        {/* Risks */}
                        {rec.risks && rec.risks.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <ExclamationCircleIcon className="h-4 w-4 text-amber-600" />
                              <span className="text-sm font-semibold text-gray-700">Consider:</span>
                            </div>
                            <ul className="text-sm text-gray-600 space-y-1 ml-6">
                              {rec.risks.slice(0, 2).map((risk, ridx) => (
                                <li key={ridx} className="list-disc">{risk}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Comparison Chart */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ChartBarIcon className="h-5 w-5 text-gray-400" />
                  Suitability Comparison
                </h3>
              </div>
              <div className="p-6">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={recommendationData.recommendations}
                      layout="vertical"
                      margin={{ left: 100, right: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" domain={[0, 100]} tickLine={false} axisLine={false} />
                      <YAxis dataKey="name" type="category" tickLine={false} axisLine={false} />
                      <Tooltip
                        formatter={(value: number) => [`${value.toFixed(0)}/100`, 'Score']}
                        contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                      />
                      <Bar
                        dataKey="suitabilityScore"
                        fill="url(#greenGradient)"
                        radius={[0, 8, 8, 0]}
                        barSize={24}
                      />
                      <defs>
                        <linearGradient id="greenGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#22c55e" />
                          <stop offset="100%" stopColor="#10b981" />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== CROP HISTORY VIEW ==================== */}
        {viewMode === 'crop-history' && selectedFarmId && cropHistoryData && !cropHistoryLoading && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Most Common Crop</span>
                  <span className="text-2xl">{getCropEmoji(cropHistoryData.summary.mostCommonCrop)}</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 capitalize">
                  {cropHistoryData.summary.mostCommonCrop.replace('_', ' ')}
                </div>
                <p className="text-sm text-gray-500 mt-2">Dominant crop over {cropHistoryData.yearsAnalyzed} years</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Crop Diversity</span>
                  <div className="w-10 h-10">
                    <RadialBarChart
                      width={40}
                      height={40}
                      cx={20}
                      cy={20}
                      innerRadius={12}
                      outerRadius={18}
                      barSize={6}
                      data={[{ value: cropHistoryData.summary.diversityScore, fill: '#8b5cf6' }]}
                      startAngle={90}
                      endAngle={-270}
                    >
                      <RadialBar background={{ fill: '#e5e7eb' }} dataKey="value" />
                    </RadialBarChart>
                  </div>
                </div>
                <div className="text-3xl font-bold text-purple-600">
                  {cropHistoryData.summary.diversityScore}%
                </div>
                <p className="text-sm text-gray-500 mt-2">Variety in crop selection</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Consistency Score</span>
                  <div className="w-10 h-10">
                    <RadialBarChart
                      width={40}
                      height={40}
                      cx={20}
                      cy={20}
                      innerRadius={12}
                      outerRadius={18}
                      barSize={6}
                      data={[{ value: cropHistoryData.summary.consistencyScore, fill: '#22c55e' }]}
                      startAngle={90}
                      endAngle={-270}
                    >
                      <RadialBar background={{ fill: '#e5e7eb' }} dataKey="value" />
                    </RadialBarChart>
                  </div>
                </div>
                <div className="text-3xl font-bold text-green-600">
                  {cropHistoryData.summary.consistencyScore}%
                </div>
                <p className="text-sm text-gray-500 mt-2">Detection accuracy</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Rotation Pattern</span>
                  <ArrowPathIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {cropHistoryData.rotationPattern || 'No clear pattern'}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {cropHistoryData.rotationPattern ? 'Detected rotation cycle' : 'Varied planting'}
                </p>
              </div>
            </div>

            {/* Crop History Timeline */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <ClipboardDocumentListIcon className="h-5 w-5" />
                  Detected Crop History Timeline
                </h3>
                <p className="text-amber-100 text-sm">Based on satellite NDVI pattern analysis</p>
              </div>

              <div className="p-6">
                <div className="relative">
                  {/* Timeline Line */}
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-500 via-orange-500 to-yellow-500" />

                  {/* Timeline Items */}
                  <div className="space-y-6">
                    {cropHistoryData.detectedCrops.map((crop, idx) => (
                      <div key={idx} className="relative pl-20">
                        {/* Year Badge */}
                        <div className="absolute left-0 w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center border-2 border-amber-300 shadow-md">
                          <div className="text-center">
                            <div className="text-xs text-amber-600 font-medium">Year</div>
                            <div className="text-lg font-bold text-amber-900">{crop.year}</div>
                          </div>
                        </div>

                        {/* Crop Card */}
                        <div className="bg-gradient-to-br from-white to-amber-50 rounded-xl border border-amber-200 p-4 hover:shadow-lg transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">{getCropEmoji(crop.cropId)}</span>
                              <div>
                                <h4 className="font-bold text-gray-900">{crop.cropName}</h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getConfidenceColor(crop.confidence)}`}>
                                    {Math.round(crop.confidence * 100)}% confidence
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    Peak: {getMonthName(crop.peakMonth)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-500">Peak NDVI</div>
                              <div className={`text-lg font-bold ${getNdviColor(crop.peakNdvi)}`}>
                                {crop.peakNdvi.toFixed(3)}
                              </div>
                            </div>
                          </div>

                          {/* Alternative Crops */}
                          {crop.alternativeCrops && crop.alternativeCrops.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-amber-100">
                              <span className="text-xs text-gray-500">Alternatives: </span>
                              {crop.alternativeCrops.map((alt, aidx) => (
                                <span key={aidx} className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                                  {getCropEmoji(alt.cropId)} {alt.cropName} ({Math.round(alt.confidence * 100)}%)
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Notes */}
                          {crop.notes && (
                            <p className="mt-2 text-sm text-gray-600 italic">{crop.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Crop Distribution Chart */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Crop Distribution Over Time</h3>
              </div>
              <div className="p-6">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cropHistoryData.detectedCrops}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="year" tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 1]} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          name === 'confidence' ? `${(value * 100).toFixed(0)}%` : value.toFixed(3),
                          name === 'confidence' ? 'Confidence' : 'Peak NDVI'
                        ]}
                        labelFormatter={(year) => {
                          const crop = cropHistoryData.detectedCrops.find(c => c.year === year);
                          return crop ? `${year}: ${crop.cropName}` : year;
                        }}
                        contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                      />
                      <Legend />
                      <Bar dataKey="confidence" name="Detection Confidence" radius={[6, 6, 0, 0]}>
                        {cropHistoryData.detectedCrops.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.confidence >= 0.8 ? '#22c55e' : entry.confidence >= 0.6 ? '#eab308' : '#f97316'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== YIELD PREDICTIONS VIEW ==================== */}
        {viewMode === 'yields' && selectedFarmId && yieldData && !yieldLoading && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Farm Area</span>
                  <MapPinIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {yieldData.areaHectares?.toFixed(1) || 'N/A'}
                  <span className="text-base font-normal text-gray-500"> ha</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Cultivatable land</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Avg NDVI</span>
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${getNdviBg(yieldData.ndviStats.mean)}`} />
                </div>
                <div className={`text-3xl font-bold ${getNdviColor(yieldData.ndviStats.mean)}`}>
                  {yieldData.ndviStats.mean.toFixed(3)}
                </div>
                <p className="text-sm text-gray-500 mt-2">Vegetation health index</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Growing Season</span>
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="text-3xl font-bold text-green-600">
                  {yieldData.climate.growingSeasonDays}
                  <span className="text-base font-normal text-gray-500"> days</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Optimal growing period</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Climate</span>
                  <SunIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="text-2xl font-bold text-orange-500">
                  {yieldData.climate.avgTemperature}°C
                </div>
                <p className="text-sm text-gray-500 mt-2">{yieldData.climate.annualPrecipitation}mm rain/yr</p>
              </div>
            </div>

            {/* Yield Predictions Grid */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-violet-500 px-6 py-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <ScaleIcon className="h-5 w-5" />
                  Expected Yields by Crop
                </h3>
                <p className="text-purple-100 text-sm">Based on {yieldData.yearsOfData} years of satellite data</p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {yieldData.predictions.map((pred, idx) => (
                    <div
                      key={idx}
                      className="bg-gradient-to-br from-gray-50 to-purple-50 rounded-xl border border-purple-100 p-4 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{getCropEmoji(pred.cropType)}</span>
                          <div>
                            <h4 className="font-bold text-gray-900 capitalize">{pred.cropType}</h4>
                            <span className="text-xs text-gray-500">{pred.unit}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-purple-700">
                            {pred.expectedYield.toFixed(0)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {pred.range.lower.toFixed(0)} - {pred.range.upper.toFixed(0)}
                          </div>
                        </div>
                      </div>

                      {/* Percentile Bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Yield Percentile</span>
                          <span>{pred.percentile}th</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pred.percentile}%`,
                              backgroundColor: getPercentileColor(pred.percentile)
                            }}
                          />
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white/60 rounded-lg p-2 text-center">
                          <div className="text-xs text-gray-500">Total Harvest</div>
                          <div className="font-semibold text-gray-900">{pred.totalHarvestTons.toFixed(1)} t</div>
                        </div>
                        <div className="bg-white/60 rounded-lg p-2 text-center">
                          <div className="text-xs text-gray-500">vs Regional</div>
                          <div className={`font-semibold ${
                            (pred.comparedToRegional || 0) >= 100 ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            {pred.comparedToRegional ? `${pred.comparedToRegional}%` : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Yield Comparison Chart */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Yield Comparison Chart</h3>
                <p className="text-sm text-gray-500">Expected yield vs regional average</p>
              </div>
              <div className="p-6">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={yieldData.predictions}
                      layout="vertical"
                      margin={{ left: 80, right: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tickLine={false} axisLine={false} />
                      <YAxis
                        dataKey="cropType"
                        type="category"
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          `${value.toFixed(0)} ${name === 'expectedYield' ? 'kg/ha' : '%'}`,
                          name === 'expectedYield' ? 'Expected Yield' : 'vs Regional Avg'
                        ]}
                        contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                      />
                      <Legend />
                      <Bar
                        dataKey="expectedYield"
                        name="Expected Yield"
                        fill="url(#purpleGradient)"
                        radius={[0, 8, 8, 0]}
                        barSize={20}
                      />
                      <defs>
                        <linearGradient id="purpleGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#8b5cf6" />
                          <stop offset="100%" stopColor="#a78bfa" />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Regional Comparison */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Performance vs Regional Average</h3>
              </div>
              <div className="p-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={yieldData.predictions.filter(p => p.comparedToRegional)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="cropType"
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                      />
                      <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        formatter={(value: number) => [`${value}%`, 'vs Regional Average']}
                        contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                      />
                      <Bar dataKey="comparedToRegional" radius={[6, 6, 0, 0]} barSize={40}>
                        {yieldData.predictions.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={(entry.comparedToRegional || 0) >= 100 ? '#22c55e' : (entry.comparedToRegional || 0) >= 80 ? '#eab308' : '#ef4444'}
                          />
                        ))}
                      </Bar>
                      {/* 100% baseline */}
                      <Line
                        type="monotone"
                        dataKey={() => 100}
                        stroke="#6b7280"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        dot={false}
                        name="Regional Average"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-gray-600">Above average (100%+)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="text-gray-600">Near average (80-100%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-gray-600">Below average (&lt;80%)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== HISTORICAL ANALYSIS VIEW ==================== */}
        {viewMode === 'history' && historyData && !historyLoading && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Average NDVI</span>
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${getNdviBg(historyData.overallStats.meanNdvi)}`} />
                </div>
                <div className={`text-3xl font-bold ${getNdviColor(historyData.overallStats.meanNdvi)}`}>
                  {historyData.overallStats.meanNdvi.toFixed(3)}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {historyData.interpretation.healthStatus}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">NDVI Range</span>
                  <ChartBarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {historyData.overallStats.minNdvi.toFixed(2)}
                  <span className="text-lg text-gray-400 mx-1">-</span>
                  {historyData.overallStats.maxNdvi.toFixed(2)}
                </div>
                <p className="text-sm text-gray-500 mt-2">Min to Max values</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Trend</span>
                  {getTrendIcon(historyData.overallStats.trend)}
                </div>
                <div className={`text-3xl font-bold ${
                  historyData.overallStats.trend > 0.01 ? 'text-green-600' :
                  historyData.overallStats.trend < -0.01 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {historyData.overallStats.trend > 0 ? '+' : ''}
                  {(historyData.overallStats.trend * 100).toFixed(2)}%
                  <span className="text-base font-normal">/yr</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {historyData.interpretation.trend}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Data Source</span>
                  <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="text-2xl font-bold text-gray-900 capitalize">
                  {historyData.dataSource}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {historyData.startYear} - {historyData.endYear}
                </p>
              </div>
            </div>

            {/* NDVI Trend Chart */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Vegetation Index (NDVI) Over Time</h3>
                <p className="text-sm text-gray-500">Track vegetation health trends across years</p>
              </div>
              <div className="p-6">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={historyData.yearlyData}>
                      <defs>
                        <linearGradient id="ndviGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="year" tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 1]} tickLine={false} axisLine={false} />
                      <Tooltip
                        formatter={(value: number) => [value.toFixed(3), 'NDVI']}
                        contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="maxNdvi"
                        stroke="#22c55e"
                        fill="url(#ndviGradient)"
                        strokeWidth={2}
                        name="Max NDVI"
                      />
                      <Line
                        type="monotone"
                        dataKey="avgNdvi"
                        stroke="#16a34a"
                        strokeWidth={3}
                        dot={{ fill: '#16a34a', r: 4 }}
                        name="Avg NDVI"
                      />
                      <Line
                        type="monotone"
                        dataKey="minNdvi"
                        stroke="#dc2626"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        name="Min NDVI"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Monthly Timeline Chart */}
            {timelineData && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900">Monthly Vegetation Pattern</h3>
                  <p className="text-sm text-gray-500">Seasonal patterns with precipitation data</p>
                </div>
                <div className="p-6">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={timelineData.timeline}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tickLine={false} axisLine={false} />
                        <YAxis yAxisId="left" domain={[0, 1]} tickLine={false} axisLine={false} />
                        <YAxis yAxisId="right" orientation="right" tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} />
                        <Legend />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="ndvi"
                          stroke="#16a34a"
                          strokeWidth={2}
                          dot={false}
                          name="NDVI"
                        />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="evi"
                          stroke="#2563eb"
                          strokeWidth={2}
                          dot={false}
                          name="EVI"
                        />
                        <Bar
                          yAxisId="right"
                          dataKey="precipitation"
                          fill="#93c5fd"
                          opacity={0.6}
                          name="Precipitation (mm)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Land Use Changes */}
            {historyData.changes.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                    Detected Land Changes
                  </h3>
                </div>
                <div className="p-6 space-y-3">
                  {historyData.changes.map((change, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-xl border-l-4 ${
                        change.type === 'vegetation_increase'
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-500'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-500'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-900">{change.year}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          change.type === 'vegetation_increase'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {(change.confidence * 100).toFixed(0)}% confidence
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{change.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Year-by-Year Comparison */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Year-by-Year Comparison</h3>
              </div>
              <div className="p-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={historyData.yearlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="year" tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 1]} tickLine={false} axisLine={false} />
                      <Tooltip
                        formatter={(value: number) => [value.toFixed(3), 'NDVI']}
                        contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                      />
                      <Bar
                        dataKey="avgNdvi"
                        fill="url(#yearGradient)"
                        radius={[6, 6, 0, 0]}
                        name="Average NDVI"
                      />
                      <defs>
                        <linearGradient id="yearGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#22c55e" />
                          <stop offset="100%" stopColor="#16a34a" />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}
        </div> {/* End of contentRef for PDF capture */}
      </div>
    </div>
  );
}
